import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { AlertTriangle, Check, ChevronRight, Loader2 } from 'lucide-react';
import client from '../api/client';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

export default function ApplyDietTemplateSheet({ open, onOpenChange, clientId, clientName, existingSuggestions = [], onSuccess }) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setSelectedTemplate(null);
      setSelectedDays([]);
      client.get('/diet-templates')
        .then(r => setTemplates(r.data || []))
        .catch(() => toast.error(t('dietTemplate.loadFailed')))
        .finally(() => setLoading(false));
    }
  }, [open]);

  const pickTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setSelectedDays([]);
  };

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const getMealsSummary = (tpl) => {
    const count = tpl.meals?.length || 0;
    if (count === 0) return 'No meals configured';
    const totalCal = (tpl.meals || []).reduce((sum, m) =>
      sum + (m.foods || []).reduce((s, f) => s + (f.calories || 0) * (f.servings || 1), 0), 0
    );
    return `${count} meal${count !== 1 ? 's' : ''} · ${Math.round(totalCal)} kcal`;
  };

  const handleApply = async () => {
    if (!selectedDays.length) return;
    setApplying(true);
    try {
      await client.post(`/diet-templates/${selectedTemplate.id}/apply`, {
        client_id: clientId,
        days: selectedDays,
      });
      toast.success(t('dietTemplate.applied', { count: selectedDays.length }));
      onSuccess?.();
      onOpenChange(false);
    } catch {
      toast.error(t('dietTemplate.applyFailed'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80dvh] overflow-y-auto" zIndex={250}>
        <SheetHeader>
          <SheetTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {selectedTemplate
              ? `${selectedTemplate.name} — ${t('dietTemplate.chooseDays')}`
              : t('dietTemplate.applyTo', { name: clientName })}
          </SheetTitle>
        </SheetHeader>

        <div className="py-4 space-y-3">
          {!selectedTemplate ? (
            loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No templates yet. Create one in Diet Templates.
              </p>
            ) : (
              templates.map(tpl => (
                <button
                  key={tpl.id}
                  onClick={() => pickTemplate(tpl)}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted active:bg-muted transition-colors text-left"
                >
                  <div>
                    <p className="text-sm font-medium">{tpl.name}</p>
                    <p className="text-xs text-muted-foreground">{getMealsSummary(tpl)}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </button>
              ))
            )
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Select which days to apply this template to. One diet suggestion will be created per day.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {DAYS.map(day => (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    className={`flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all ${
                      selectedDays.includes(day)
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-muted/30'
                    }`}
                  >
                    <span className="text-xs font-medium">{DAY_LABELS[day]}</span>
                    {selectedDays.includes(day) && (
                      <Check className="h-3 w-3 text-primary mt-0.5" />
                    )}
                  </button>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                {selectedDays.length} day{selectedDays.length !== 1 ? 's' : ''} selected
              </p>

              {(() => {
                const existingDays = new Set();
                existingSuggestions.forEach(s => (s.days_of_week || []).forEach(d => existingDays.add(d)));
                const overlapping = selectedDays.filter(d => existingDays.has(d));
                if (overlapping.length === 0) return null;
                return (
                  <div className="flex items-start gap-2 p-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-500">
                      {overlapping.map(d => DAY_LABELS[d]).join(', ')} already {overlapping.length === 1 ? 'has' : 'have'} a diet suggestion. The client will see multiple plans for {overlapping.length === 1 ? 'that day' : 'those days'}.
                    </p>
                  </div>
                );
              })()}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setSelectedTemplate(null)}>
                  {t('common.back')}
                </Button>
                <Button
                  className="flex-1 bg-primary hover:bg-primary/90"
                  onClick={handleApply}
                  disabled={applying || !selectedDays.length}
                >
                  {applying && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {t('dietTemplate.applyToDays', { count: selectedDays.length })}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
